import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { theme } from '../styles/MUITheme';
import styled from '@emotion/styled';
import { 
  Box, 
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LINE_TYPES } from '../firebase/schema';
import { getMockupUrl } from '../utils/fileHelpers';
import PressSetupPanel from '../components/PressSetupPanel';
import { findInkInCatalog, isUnderbaseColor } from '../utils/pressSetup';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';

const PageContainer = styled(Box)`
  display: flex;
  height: calc(100vh - 60px);
  overflow: hidden;
  font-family: 'Montserrat', sans-serif;
  background: var(--al-bg);
`;

const LeftPanel = styled(Box)`
  width: 420px;
  min-width: 420px;
  height: 100%;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  background: var(--al-bg);
  overflow: hidden;
  min-height: 0;
`;

const LineList = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const RightPanel = styled(Box)`
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--al-bg);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 32px 40px;
  max-width: calc(100vw - 420px);
`;

const Header = styled(Box)`
  padding: 24px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: #222;
`;

const LineItem = styled(Box)`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  ${props => props.selected && `
    background: rgba(var(--al-primary-rgb), 0.15);
    border-left: 4px solid var(--al-primary);
    padding-left: 16px;
  `}
`;

const LineTitle = styled(Typography)`
  font-weight: 600;
  color: white;
  margin-bottom: 6px;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  line-height: 1.3;
`;

const LineSubtitle = styled(Typography)`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.4;
`;

const DetailHeader = styled(Box)`
  margin-top: 20px;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const MockupContainer = styled(Box)`
  background: #222;
  border: 2px solid rgba(255, 255, 255, 0.15);
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  max-height: 500px;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const MockupImage = styled.img`
  max-width: 100%;
  max-height: 450px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
`;

const ContentGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 32px;
  margin-bottom: 32px;
`;

const PressSetupSection = styled(Box)`
  padding: 16px;
  background: #222;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
`;

const InkNotesSection = styled(Box)`
  margin-top: 24px;
  padding: 20px;
  background: #222;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
`;

const InkNotesTextArea = styled(TextField)`
  & .MuiOutlinedInput-root {
    background-color: #363636;
    color: white;
    border-radius: 0;
    
    & fieldset {
      border-color: rgba(255, 255, 255, 0.2);
      border-width: 1px;
    }
    
    &:hover fieldset {
      border-color: rgba(var(--al-primary-rgb), 0.5);
    }
    
    &.Mui-focused fieldset {
      border-color: var(--al-primary);
      border-width: 2px;
    }
  }
  
  & .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    text-transform: uppercase;
    
    &.Mui-focused {
      color: var(--al-primary);
    }
  }
  
  & .MuiInputBase-input {
    color: white;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const SaveButton = styled(Button)`
  background: var(--al-primary);
  color: var(--al-bg);
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 0;
  padding: 10px 24px;
  
  &:hover {
    background: #7FE87F;
  }
  
  &:disabled {
    background: rgba(var(--al-primary-rgb), 0.3);
    color: rgba(0, 0, 0, 0.5);
  }
`;

const getLineInkColors = (line) => [
  ...(line.frontInkColors || []),
  ...(line.backInkColors || []),
  ...(line.otherInkColors || [])
];

const lineMatchesSearch = (line, query) => {
  if (!query) return true;
  const haystack = [
    line.designName,
    line.customerName,
    String(line.orderNumber ?? ''),
    line.garmentId,
    line.garmentColor,
    ...getLineInkColors(line)
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
};

const InkManager = () => {
  const { facilityId } = useFacility();
  const [lines, setLines] = useState([]);
  const [inkInventory, setInkInventory] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [inkNotes, setInkNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [isSavingPressSetup, setIsSavingPressSetup] = useState(false);
  const [pressSetupSaved, setPressSetupSaved] = useState(false);
  const rightPanelRef = useRef(null);

  // Fetch ink inventory
  useEffect(() => {
    const fetchInks = async () => {
      try {
        const inksRef = collection(db, 'inks');
        const snapshot = await getDocs(inksRef);
        const inksList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInkInventory(inksList);
      } catch (error) {
        console.error('Error fetching inks:', error);
      }
    };
    fetchInks();
  }, []);

  const needsInkAssignment = useCallback((color) => {
    if (!color || color.trim() === '') return false;
    if (isUnderbaseColor(color)) return false;
    return !findInkInCatalog(color, inkInventory);
  }, [inkInventory]);

  // Fetch lines
  useEffect(() => {
    const fetchLines = async () => {
      try {
        setIsLoading(true);
        const linesRef = collection(db, 'orderLines');
        const q = query(
          linesRef,
          where('type', '==', 'screen')
        );
        const snapshot = await getDocs(q);
        
        const linesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter client-side for 'Screen Print' type AND exclude Printed/Completed statuses
        const screenPrintLines = linesList.filter(line => {
          const isScreenPrint = line.type === 'Screen Print' || line.type === 'screen' || line.type === LINE_TYPES.SCREEN_PRINT;
          const isPrintedOrCompleted = line.status === 'Printed' || line.status === 'Completed';
          return isScreenPrint && !isPrintedOrCompleted;
        });
        
        // Fetch order data to get customer names
        const orderIds = [...new Set(screenPrintLines.map(line => line.orderId).filter(id => id))];
        const orderDocs = await Promise.all(
          orderIds.map(async orderId => {
            try {
              const orderRef = doc(db, 'orders', orderId);
              const orderSnap = await getDoc(orderRef);
              return { id: orderId, ...orderSnap.data() };
            } catch (error) {
              console.error(`Error fetching order ${orderId}:`, error);
              return null;
            }
          })
        );
        
        const orderMap = new Map(
          orderDocs.filter(order => order !== null).map(order => [order.id, order])
        );
        
        // Combine line and order data; facility falls back to parent order
        const linesWithOrder = screenPrintLines.map(line => {
          const order = orderMap.get(line.orderId);
          return {
            ...line,
            facilityId: line.facilityId || order?.facilityId,
            customerName: order?.customerName || 'N/A',
            orderNumber: order?.poNumber || line.orderNumber || 'N/A'
          };
        });

        const facilityLines = filterByFacility(linesWithOrder, facilityId);
        setLines(facilityLines);
        setSelectedLine((prev) =>
          prev && facilityLines.some((l) => l.id === prev.id)
            ? facilityLines.find((l) => l.id === prev.id)
            : null
        );
      } catch (error) {
        console.error('Error fetching lines:', error);
        // Set empty array on error so the component still renders
        setLines([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Always fetch lines, even if inkInventory is empty (we'll just show all lines)
    fetchLines();
  }, [inkInventory, facilityId]);

  const lineNeedsReview = useCallback((line) => {
    return getLineInkColors(line).some((color) => needsInkAssignment(color));
  }, [needsInkAssignment]);

  const linesNeedingReviewCount = useMemo(
    () => lines.filter((line) => lineNeedsReview(line)).length,
    [lines, lineNeedsReview]
  );

  const filteredLines = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const matched = query
      ? lines.filter((line) => lineMatchesSearch(line, query))
      : lines;

    return [...matched].sort((a, b) => {
      const aNeedsReview = lineNeedsReview(a) ? 0 : 1;
      const bNeedsReview = lineNeedsReview(b) ? 0 : 1;
      if (aNeedsReview !== bNeedsReview) return aNeedsReview - bNeedsReview;

      const aLabel = `${a.customerName || ''} ${a.designName || ''}`.trim().toLowerCase();
      const bLabel = `${b.customerName || ''} ${b.designName || ''}`.trim().toLowerCase();
      if (aLabel !== bLabel) return aLabel.localeCompare(bLabel);

      return String(a.orderNumber ?? '').localeCompare(String(b.orderNumber ?? ''), undefined, {
        numeric: true
      });
    });
  }, [lines, searchQuery, lineNeedsReview]);

  // Update inkNotes when selectedLine changes
  useEffect(() => {
    if (selectedLine) {
      setInkNotes(selectedLine.inkNotes || '');
      setNotesSaved(false);
    }
  }, [selectedLine?.id]);

  useEffect(() => {
    if (selectedLine?.id && rightPanelRef.current) {
      rightPanelRef.current.scrollTop = 0;
    }
  }, [selectedLine?.id]);

  const handlePressSetupSave = async (pressSetup) => {
    if (!selectedLine) return;

    setIsSavingPressSetup(true);
    try {
      await updateDoc(doc(db, 'orderLines', selectedLine.id), {
        pressSetup,
        updatedAt: serverTimestamp()
      });

      const updatedLine = { ...selectedLine, pressSetup };
      setSelectedLine(updatedLine);
      setLines((prev) => prev.map((line) =>
        line.id === selectedLine.id ? updatedLine : line
      ));

      setPressSetupSaved(true);
      setTimeout(() => setPressSetupSaved(false), 2000);
    } catch (error) {
      console.error('Error saving press setup:', error);
      alert('Failed to save press setup');
    } finally {
      setIsSavingPressSetup(false);
    }
  };

  const handleSaveInkNotes = async () => {
    if (!selectedLine) return;
    
    setIsSavingNotes(true);
    try {
      await updateDoc(doc(db, 'orderLines', selectedLine.id), {
        inkNotes: inkNotes,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      const updatedLine = { ...selectedLine, inkNotes };
      setSelectedLine(updatedLine);
      setLines(prev => prev.map(line => 
        line.id === selectedLine.id ? updatedLine : line
      ));
      
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (error) {
      console.error('Error saving ink notes:', error);
      alert('Failed to save ink notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Track pending updates to prevent loops
  const pendingUpdatesRef = useRef(new Map());
  
  // Update line ink colors
  const handleInkUpdate = useCallback(async (location, colorIndex, newColor, saveToDb = true) => {
    if (!selectedLine) return;
    
    const colorsArrayKey = `${location.toLowerCase()}InkColors`;
    const updateKey = `${selectedLine.id}-${colorsArrayKey}-${colorIndex}`;
    
    // Prevent duplicate Firestore writes only (not local keystrokes)
    if (saveToDb && pendingUpdatesRef.current.has(updateKey)) {
      return;
    }
    
    if (saveToDb) {
      pendingUpdatesRef.current.set(updateKey, true);
    }
    
    const currentColors = [...(selectedLine[colorsArrayKey] || [])];
    // Ensure we always set a string value (empty string if cleared, not null/undefined)
    // Make sure the array is long enough to hold this index
    while (currentColors.length <= colorIndex) {
      currentColors.push('');
    }
    currentColors[colorIndex] = newColor || '';
    
    const updatedLine = {
      ...selectedLine,
      [colorsArrayKey]: currentColors
    };
    
    // Update local state immediately for responsive UI
    setLines(prev => prev.map(line => 
      line.id === selectedLine.id ? updatedLine : line
    ));
    setSelectedLine(updatedLine);
    
    // Only save to database if requested (not on every keystroke)
    if (saveToDb) {
      try {
        // When saving to Firestore, filter out trailing empty strings but keep the structure
        // This means if we have ['RED', '', 'BLUE'], we save ['RED', '', 'BLUE']
        // But if we have ['RED', '', ''], we save ['RED']
        let colorsToSave = [...currentColors];
        // Remove trailing empty strings
        while (colorsToSave.length > 0 && (!colorsToSave[colorsToSave.length - 1] || colorsToSave[colorsToSave.length - 1].trim() === '')) {
          colorsToSave.pop();
        }
        // But preserve empty strings in the middle (they represent cleared slots)
        // So ['RED', '', 'BLUE'] becomes ['RED', '', 'BLUE'] not ['RED', 'BLUE']
        
        await updateDoc(doc(db, 'orderLines', selectedLine.id), {
          [colorsArrayKey]: colorsToSave.length > 0 ? colorsToSave : [],
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error updating line:', error);
        alert('Failed to update ink color');
      }
    }
    
    // Clear the pending update flag after a short delay
    if (saveToDb) {
      setTimeout(() => {
        pendingUpdatesRef.current.delete(updateKey);
      }, 100);
    }
  }, [selectedLine]);

  return (
    <MUIThemeProvider theme={theme}>
      <PageContainer>
        <LeftPanel>
          <Header>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white', 
                fontWeight: 700, 
                mb: 2, 
                textTransform: 'uppercase',
                fontSize: '1.25rem',
                letterSpacing: '1px'
              }}
            >
              Ink Manager
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search customer, design, PO, style, color, ink..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '1.1rem' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#363636',
                  color: 'white',
                  borderRadius: 0,
                  fontSize: '0.8rem',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(var(--al-primary-rgb), 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--al-primary)' }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.4)',
                  opacity: 1,
                  textTransform: 'none'
                }
              }}
            />
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '0.7rem', textTransform: 'uppercase' }}>
              {filteredLines.length} active job{filteredLines.length === 1 ? '' : 's'}
              {linesNeedingReviewCount > 0 && (
                <> · {linesNeedingReviewCount} need review</>
              )}
            </Typography>
          </Header>
          
          {isLoading ? (
            <LineList sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <CircularProgress />
            </LineList>
          ) : (
            <LineList>
              {filteredLines.length === 0 ? (
                <Box sx={{ padding: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    {searchQuery.trim()
                      ? 'No active jobs match your search'
                      : 'No active screen print jobs found'}
                  </Typography>
                </Box>
              ) : (
                filteredLines.map(line => {
                  const needsReview = lineNeedsReview(line);
                  const hasInkNotes = !!line.inkNotes;
                  
                  return (
                    <LineItem
                      key={line.id}
                      selected={selectedLine?.id === line.id}
                      onClick={() => setSelectedLine(line)}
                    >
                      <LineTitle>
                        {line.customerName && line.customerName !== 'N/A' 
                          ? `${line.customerName} - ${line.designName || 'Untitled Design'}`
                          : line.designName || 'Untitled Design'}
                      </LineTitle>
                      <LineSubtitle>
                        {line.orderNumber || 'No Order'} • {line.garmentId || 'No Style'} • {line.garmentColor || 'No Color'}
                      </LineSubtitle>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: needsReview || hasInkNotes ? 1.5 : 0 }}>
                        {needsReview && (
                          <Chip
                            label="Needs Review"
                            size="small"
                            sx={{
                              backgroundColor: '#FFC107',
                              color: '#000',
                              fontSize: '0.65rem',
                              height: '22px',
                              fontWeight: 600,
                              borderRadius: 0
                            }}
                          />
                        )}
                        {hasInkNotes && (
                          <Chip
                            label="📝 Has Notes"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(var(--al-primary-rgb), 0.2)',
                              color: 'var(--al-primary)',
                              fontSize: '0.65rem',
                              height: '22px',
                              fontWeight: 600,
                              borderRadius: 0,
                              border: '1px solid rgba(var(--al-primary-rgb), 0.3)'
                            }}
                          />
                        )}
                      </Box>
                    </LineItem>
                  );
                })
              )}
            </LineList>
          )}
        </LeftPanel>
        
        <RightPanel ref={rightPanelRef}>
          {selectedLine ? (
            <>
              <DetailHeader>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'white', 
                    mb: 1.5, 
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  {selectedLine.customerName && selectedLine.customerName !== 'N/A' 
                    ? `${selectedLine.customerName} - ${selectedLine.designName || 'Untitled Design'}`
                    : selectedLine.designName || 'Untitled Design'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <strong style={{ color: 'var(--al-primary)' }}>Invoice:</strong> {selectedLine.orderNumber || 'N/A'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <strong style={{ color: 'var(--al-primary)' }}>Style:</strong> {selectedLine.garmentId || 'N/A'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <strong style={{ color: 'var(--al-primary)' }}>Color:</strong> {selectedLine.garmentColor || 'N/A'}
                  </Typography>
                  <Typography sx={{ 
                    color: 'var(--al-primary)', 
                    fontSize: '0.875rem', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.5px',
                    fontWeight: 700,
                    backgroundColor: 'rgba(var(--al-primary-rgb), 0.15)',
                    padding: '4px 12px',
                    borderRadius: '2px'
                  }}>
                    <strong>Total Qty:</strong> {selectedLine.totalQuantity || Object.values(selectedLine.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0) || 0}
                  </Typography>
                </Box>
              </DetailHeader>
              
              <ContentGrid>
                {/* Left side: Mockup */}
                {(() => {
                  // Use consistent helper to get mockup URL
                  const mockupUrl = getMockupUrl(selectedLine);
                  
                  return mockupUrl ? (
                    <MockupContainer>
                      <Box sx={{ width: '100%', textAlign: 'center' }}>
                        <Typography 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            mb: 2, 
                            textTransform: 'uppercase',
                            fontSize: '0.875rem',
                            letterSpacing: '1px',
                            fontWeight: 600
                          }}
                        >
                          Mockup Preview
                        </Typography>
                        <MockupImage 
                          src={mockupUrl} 
                          alt={selectedLine.designName || 'Mockup'}
                          onError={(e) => {
                            console.error('Error loading mockup image:', mockupUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    </MockupContainer>
                  ) : (
                    <MockupContainer>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', textTransform: 'uppercase' }}>
                        No Mockup Available
                      </Typography>
                    </MockupContainer>
                  );
                })()}
                
                <PressSetupSection>
                  <Typography
                    sx={{
                      color: 'white',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.9rem',
                      letterSpacing: '0.5px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      paddingBottom: '6px'
                    }}
                  >
                    Press Setup
                  </Typography>
                  <PressSetupPanel
                    key={selectedLine.id}
                    lineData={selectedLine}
                    pressSetup={selectedLine.pressSetup}
                    inkCatalog={inkInventory}
                    editableInks
                    needsInkReview={needsInkAssignment}
                    onInkColorChange={(location, colorIndex, newColor, saveToDb) => {
                      const locationLabel = location.charAt(0).toUpperCase() + location.slice(1);
                      handleInkUpdate(locationLabel, colorIndex, newColor, saveToDb);
                    }}
                    onSave={handlePressSetupSave}
                    showPressNotes={false}
                    showSaveButton
                    saveLabel="Save Press Setup"
                    isSaving={isSavingPressSetup}
                    compact
                  />
                  {pressSetupSaved && (
                    <Typography sx={{ color: 'var(--al-primary)', fontSize: '0.8rem', fontWeight: 600, mt: 1.5 }}>
                      ✓ Press Setup Saved
                    </Typography>
                  )}
                </PressSetupSection>
              </ContentGrid>
              
              {/* Ink Notes Section */}
              <InkNotesSection>
                <Typography 
                  sx={{ 
                    color: 'white', 
                    mb: 2, 
                    fontWeight: 700, 
                    textTransform: 'uppercase',
                    fontSize: '0.9rem',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  Ink Notes
                  {selectedLine.inkNotes && (
                    <Chip
                      label="Has Notes"
                      size="small"
                      sx={{
                        backgroundColor: 'var(--al-primary)',
                        color: 'var(--al-bg)',
                        fontSize: '0.6rem',
                        height: '20px',
                        fontWeight: 600,
                        borderRadius: 0
                      }}
                    />
                  )}
                </Typography>
                <Typography 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    fontSize: '0.75rem',
                    mb: 2,
                    lineHeight: 1.4
                  }}
                >
                  Add special instructions for printers about ink mixing, pantone matching, or other details.
                </Typography>
                <InkNotesTextArea
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="e.g., Mix 50% White + 50% Athletic Gold for gold on dark garments. Use soft hand additive. Pantone 123C reference."
                  value={inkNotes}
                  onChange={(e) => {
                    setInkNotes(e.target.value);
                    setNotesSaved(false);
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                  <SaveButton
                    onClick={handleSaveInkNotes}
                    disabled={isSavingNotes || (inkNotes === (selectedLine.inkNotes || ''))}
                  >
                    {isSavingNotes ? 'Saving...' : 'Save Notes'}
                  </SaveButton>
                  {notesSaved && (
                    <Typography sx={{ color: 'var(--al-primary)', fontSize: '0.8rem', fontWeight: 600 }}>
                      ✓ Notes Saved
                    </Typography>
                  )}
                  {inkNotes !== (selectedLine.inkNotes || '') && !notesSaved && (
                    <Typography sx={{ color: '#FFC107', fontSize: '0.75rem' }}>
                      Unsaved changes
                    </Typography>
                  )}
                </Box>
              </InkNotesSection>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 2
            }}>
              <Typography sx={{ 
                color: 'rgba(255, 255, 255, 0.4)', 
                fontSize: '1rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Select a line from the left
              </Typography>
              <Typography sx={{ 
                color: 'rgba(255, 255, 255, 0.3)', 
                fontSize: '0.875rem'
              }}>
                to view and edit ink colors
              </Typography>
            </Box>
          )}
        </RightPanel>
      </PageContainer>
    </MUIThemeProvider>
  );
};

export default InkManager;

